import React, { useState, useEffect, useRef } from "react";
import StepWizard from "react-step-wizard";
import { ReactComponent as Trophy } from "../img/trophy-icon.svg";
import { useParams, useLoaderData } from "react-router-dom";

import correctSound from "../audio/correctSound.mp3";

import { MejorateUpdate } from "./MejorateCreate";

// Credito Paciente
import { CreditoUpdate } from "../components/CreditoUpdate";
import * as APIUSER from "../services/user";

import { ReactComponent as StarSpark } from "../img/icon-start-sparks.svg";

import * as APIMEJORATE from "../services/mejorate";

import { createGananciasLog } from "../services/gananciaslog";

import { GananciasModal } from "../components/GananciasModal";

/**
 * Componente IntroStep
 * Muestra la introducción al cuestionario y un botón para comenzar o continuar.
 */
const IntroStep = ({
  nextStep,
  quizType,
  mejorateStep,
  goToStep,
  completed,
}) => {
  const handleComenzar = async () => {
    if (mejorateStep !== 0 && !completed) {
      goToStep(mejorateStep + 1); // Ir al paso específico. Sumar 1 para saltar última respuesta dada
      console.log(`Navegando al paso ${mejorateStep}`);
    } else {
      nextStep(); // Ir al siguiente paso
      console.log("Navegando al siguiente paso");
    }
  };

  const buttonLabel = mejorateStep > 0 ? "Continuar" : "Empezar";

  return (
    <div
      className="intro-step"
      style={{ textAlign: "center", padding: "20px" }}
    >
      <StarSpark
        style={{ width: "80px", height: "80px", marginBottom: "20px" }}
      />
      <h2 className="is-size-2 has-text-weight-semibold mb-4">
        ¡Bienvenido/a a Mejórate!
      </h2>
      <p style={{ marginBottom: "10px" }}>
        Te invitamos a participar en este breve test interactivo diseñado para
        apoyar tu recuperación. Ponte a prueba y descubre conocimientos y
        estrategias para tu bienestar.
      </p>
      <p style={{ marginBottom: "10px" }}>
        ¡Cada respuesta correcta te acercará un paso más hacia tus objetivos!
        Comienza ahora y disfruta del camino hacia una mejor calidad de vida.
      </p>
      <p style={{ marginBottom: "10px" }}>
        ¡Ah!, y por cada respuesta correcta añade 0,50€ a tus Ganancias.
      </p>
      <p>¡Mucha suerte!</p>
      <button className="button is-link mt-6" onClick={handleComenzar}>
        {buttonLabel}
      </button>
    </div>
  );
};

/**
 * Componente QuestionStep
 * Muestra una pregunta con opciones de respuesta.
 */
const QuestionStep = ({
  question,
  options,
  correctAnswerIndex,
  nextStep,
  currentStep,
  totalSteps,
  onAnswer,
  showIntro,
  isLastQuestion, // Nueva prop para identificar la última pregunta
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [canProceed, setCanProceed] = useState(false);
  // Eliminamos isDisabled ya que no lo necesitamos más

  const handleOptionChange = (event, index) => {
    setSelectedAnswer(index);
    const correct = index === correctAnswerIndex;
    setIsCorrect(correct);
    setCanProceed(correct);
    onAnswer(correct, isLastQuestion); // Pasamos los parámetros correspondientes

    // Añadir console.log para rastrear la selección de respuesta
    console.log(
      `Respuesta seleccionada: ${optionText(index)} | Correcta: ${correct}`
    );
  };

  // Función auxiliar para obtener el texto de la opción seleccionada
  const optionText = (index) => {
    if (index >= 0 && index < options.length) {
      return options[index];
    }
    return "Opción inválida";
  };

  const displayCurrentStep = showIntro ? currentStep - 1 : currentStep;
  const displayTotalSteps = showIntro ? totalSteps - 2 : totalSteps - 1;

  return (
    <div>
      <p className="questions-steps">
        Pregunta {displayCurrentStep}/{displayTotalSteps}
      </p>
      <h3>{question}</h3>
      {options.map((option, index) => (
        <label
          key={index}
          style={{
            backgroundColor:
              selectedAnswer === index && isCorrect
                ? "#90b6fc" // Fondo azul para respuesta correcta seleccionada
                : selectedAnswer === index && !isCorrect
                ? "#f8d7da" // Fondo rojo para respuesta incorrecta seleccionada
                : "transparent",
            borderColor:
              selectedAnswer === index && isCorrect
                ? "#6197fa"
                : selectedAnswer === index && !isCorrect
                ? "#f5c6cb"
                : "#e3e3e3",
            cursor: "pointer", // Mantener el cursor como puntero
            transition: "background-color 0.3s",
            color: selectedAnswer === index && isCorrect ? "#fff" : "#4a4a4a",
          }}
          onMouseEnter={(e) => {
            if (selectedAnswer !== index) {
              e.target.style.backgroundColor = "#f7f7f7"; // Color gris en hover
            }
          }}
          onMouseLeave={(e) => {
            if (selectedAnswer !== index) {
              e.target.style.backgroundColor = "transparent";
            }
          }}
        >
          <input
            type="radio"
            name="answer"
            value={option}
            onChange={(e) => handleOptionChange(e, index)}
            style={{
              accentColor:
                selectedAnswer === index && !isCorrect ? "#f44336" : "", // Rojo para incorrectas
              marginInlineEnd: "10px",
            }}
          />
          {option}
          {selectedAnswer === index && isCorrect && (
            <div className="heart"></div>
          )}
        </label>
      ))}
      <button
        className="button is-link"
        onClick={nextStep}
        disabled={!canProceed} // Desactiva el botón hasta que la respuesta sea correcta
      >
        Siguiente
      </button>
    </div>
  );
};

/**
 * Componente ResultsSummary
 * Muestra un resumen de los resultados del cuestionario.
 */
const ResultsSummary = ({ mejoratePoint, totalQuestions }) => {
  // const totalQuestions = results.length;
  // const correctAnswers = results.filter((result) => result).length;
  const correctAnswers = mejoratePoint;
  const percentage = ((correctAnswers / totalQuestions) * 100).toFixed(2);

  return (
    <div
      className="results-summary"
      style={{
        textAlign: "center",
        padding: "20px",
        // border: "1px solid #ccc",
        // borderRadius: "10px",
      }}
    >
      <h3
        style={{
          fontSize: "1.6rem",
          textTransform: "uppercase",
        }}
      >
        Resumen de Resultados
      </h3>
      <div className="trophy-icon-container">
        <Trophy />
      </div>
      <p style={{ fontSize: "18px", margin: "14px 0", fontWeight: 600 }}>
        Total de preguntas: {totalQuestions}
      </p>
      <p style={{ fontSize: "18px", margin: "14px 0", fontWeight: 600 }}>
        Total de intentos: {totalQuestions}
      </p>
      <p style={{ fontSize: "18px", margin: "14px 0", fontWeight: 600 }}>
        Respuestas correctas: {correctAnswers}
      </p>
      <p
        style={{
          fontSize: "20px",
          margin: "20px 0",
          fontWeight: "bold",
          color: "#ffffff",
          backgroundColor: "#6197fa",
          padding: "16px 26px",
          borderRadius: "32px",
          display: "inline-block",
          letterSpacing: "-0.002em",
        }}
      >
        Porcentaje de aciertos: {percentage}%
      </p>
    </div>
  );
};

/**
 * Función loader
 * Carga datos iniciales para el componente.
 */
export async function loader({ params }) {
  const token = sessionStorage.getItem("token");

  // Si no hay token, simplemente NO llamamos a la API y retornamos null
  if (!token) {
    return null;
  }
  const { tratamientoId } = params;

  const data = await APIMEJORATE.getAllMejorateByTax(params.tratamientoId);
  //const data = await APIMEJORATE.getAllMejorate();

  let firstCannabis = null;
  let firstAlcohol = null;
  let firstCocaina = null;
  let firstBenzo = null;

  for (const item of data) {
    const type = item.meta?.mejorateTipo;
    if (type === "Cannabis" && !firstCannabis) firstCannabis = item;
    if (type === "Alcohol" && !firstAlcohol) firstAlcohol = item;
    if (type === "Cocaína" && !firstCocaina) firstCocaina = item;
    if (type === "Benzodiazepinas" && !firstBenzo) firstBenzo = item;

    // Si ya encontramos los cuatro, podemos romper el bucle
    if (firstCannabis && firstAlcohol && firstCocaina && firstBenzo) break;
  }

  return {
    data,
    firstCannabis,
    firstAlcohol,
    firstCocaina,
    firstBenzo,
  };
}

/**
 * Componente Principal MejorateQuiz
 * Genérico para manejar diferentes tipos de cuestionarios.
 */
export default function MejorateQuiz({ quizType, questions }) {
  const parentData = useLoaderData();

  const [quizAvailable, setQuizAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [isGananciasModalOpen, setIsGananciasModalOpen] = useState(false);
  const [gananciasMessage, setGananciasMessage] = useState("");
  const [shouldShowModalAfterResults, setShouldShowModalAfterResults] =
    useState(false);

  // Al cargar, si no tienes un metaFecha definido en el objeto, puedes mantener la lógica de fecha actual.
  const metaFecha = new Date()
    .toISOString()
    .replace(/T.*/, "")
    .split("-")
    .join("-");
  const mejorateTipo = quizType;

  const [mejorateCompleted, setMejorateCompleted] = useState(false);
  const [initialMejorateCompleted, setInitialMejorateCompleted] =
    useState(false);
  const [mejorateStep, setMejorateStep] = useState(0);
  const [mejoratePoint, setMejoratePoint] = useState(0);
  const [isCreatingButton, setIsCreatingButton] = useState(false);

  const { tratamientoId } = useParams();

  const [credito, setCredito] = useState(0);
  const [pacienteId, setPacienteId] = useState(0);

  const [results, setResults] = useState([]);
  const audioRef = useRef(new Audio(correctSound));

  const [mejorateId, setMejorateId] = useState(null);
  const [answeredQuestions, setAnsweredQuestions] = useState(new Set());

  // Recuperar información del usuario sobre sus créditos actuales
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Obtener los datos del usuario
        const currentUserData = await APIUSER.getCurrentUser();
        if (currentUserData) {
          setCredito(currentUserData?.meta?.credito);
          setPacienteId(currentUserData?.id);
        }
      } catch (error) {
        console.error("Error al obtener los datos del usuario:", error);
      }
    };

    fetchUserData();
  }, []);

  // Asignar post ID y meta según los datos obtenidos
  useEffect(() => {
    console.log(parentData);
    if (parentData && quizType) {
      if (quizType === "Cocaína") {
        quizType = "Cocaina";
      } else if (quizType === "Benzodiazepinas") {
        quizType = "Benzo";
      }
      const firstData = parentData[`first${quizType}`];
      console.log(firstData);
      if (firstData) {
        setMejorateId(firstData.id);
        // Si hay meta, actualizamos los estados con los valores obtenidos
        if (firstData.meta) {
          setMejorateCompleted(firstData.meta.mejorateCompleted ?? false);
          setInitialMejorateCompleted(
            firstData.meta.mejorateCompleted ?? false
          );
          setMejorateStep(firstData.meta.mejorateStep ?? 0);
          setMejoratePoint(firstData.meta.mejoratePoint ?? 0);
        }
      } else {
        setQuizAvailable(false); // Cambiar estado para indicar que no hay cuestionario disponible
      }
    } else {
      setQuizAvailable(false); // Cambiar estado para indicar que no hay cuestionario disponible
    }
    setIsLoading(false);
  }, [quizType, parentData]);
  /**
   * Función handleAnswer
   * Maneja la respuesta del usuario y actualiza los estados correspondientes.
   */
  const handleAnswer = (isCorrect, isLastQuestion, questionIndex) => {
    console.log(`Respuesta ${isCorrect ? "correcta" : "incorrecta"}`);

    // Clonar los estados actuales antes de actualizar
    const currentResults = [...results];
    const currentAnswered = new Set(answeredQuestions);

    // Actualizar resultados
    currentResults[questionIndex] = isCorrect;

    // Variables temporales para los nuevos valores
    let newMejoratePoint = mejoratePoint;
    let newMejorateStep = mejorateStep;
    let newMejorateCompleted = mejorateCompleted;

    let preguntaRespondidaPorPrimeraVez = false;

    if (isCorrect) {
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .then(() => console.log("Sonido de respuesta correcta reproducido."))
        .catch((error) =>
          console.error("Error al reproducir el sonido:", error)
        );
    }

    // Si esta pregunta no se ha respondido antes
    if (!currentAnswered.has(questionIndex) && !initialMejorateCompleted) {
      preguntaRespondidaPorPrimeraVez = true;

      if (isCorrect) {
        // Incrementar punto sólo si es la primera respuesta correcta a esta pregunta
        newMejoratePoint += 1;
        console.log(`mejoratePoint incrementado a: ${newMejoratePoint}`);
      }

      // Incrementar el paso por responder una pregunta nueva
      newMejorateStep += 1;
      console.log(`mejorateStep incrementado a: ${newMejorateStep}`);

      // Marcar la pregunta como respondida
      currentAnswered.add(questionIndex);
    }

    // Si es la última pregunta
    if (isLastQuestion && !initialMejorateCompleted) {
      newMejorateCompleted = true;
      console.log("mejorateCompleted establecido a: true");
    }

    // Actualizamos los estados ahora sí
    if (!mejorateCompleted) {
      setResults(currentResults);
      setAnsweredQuestions(currentAnswered);
      setMejoratePoint(newMejoratePoint);
      setMejorateStep(newMejorateStep);
      setMejorateCompleted(newMejorateCompleted);
    }

    // Solo llamar a updateMejorate si la pregunta acaba de responderse por primera vez y si no está completo el test
    if (preguntaRespondidaPorPrimeraVez && !initialMejorateCompleted) {
      updateMejorate(
        metaFecha,
        mejorateTipo,
        newMejorateCompleted,
        newMejorateStep,
        newMejoratePoint,
        tratamientoId,
        pacienteId,
        mejorateId,
        isLastQuestion,
        initialMejorateCompleted
      );
    }
  };

  const updateMejorate = async (
    metaFecha,
    mejorateTipo,
    mejorateCompleted,
    mejorateStep,
    mejoratePoint,
    tratamientoId,
    pacienteId,
    mejorateId,
    isLastQuestion,
    initialMejorateCompleted
  ) => {
    console.log("Actualizamos entrada");
    setIsCreatingButton(true);
    console.log(`updateMejorate llamado con:`, {
      metaFecha,
      mejorateTipo,
      mejorateCompleted,
      mejorateStep,
      mejoratePoint,
      tratamientoId,
      pacienteId,
      mejorateId,
    });

    try {
      const val = await MejorateUpdate(
        metaFecha,
        mejorateTipo,
        mejorateCompleted,
        mejorateStep,
        mejoratePoint,
        tratamientoId,
        pacienteId,
        mejorateId
      );
      console.log(`Mejorate actualizado con ID: ${val.id}`);
      // Solo se ejecuta `CreditoUpdate` si `updateMejorate` fue exitoso
      if (
        val.id === mejorateId &&
        isLastQuestion &&
        !initialMejorateCompleted
      ) {
        // Cálculo de beneficios y guardado en BBDD de cantidad
        const creditoAdded = 0.5 * mejoratePoint;
        const creditoFinal =
          typeof credito === "number" ? credito + creditoAdded : creditoAdded;

        console.log(`Credito final calculado: ${creditoFinal}`);
        const creditoUpdateResult = await CreditoUpdate(
          creditoFinal,
          pacienteId
        );
        console.log("Resultado de CreditoUpdate:", creditoUpdateResult);

        // Llamada para crear el registro en gananciaslogs
        const fechaActual = new Date().toISOString();
        const logText = `Completó con éxito el cuestionario de ${mejorateTipo}, con ${mejoratePoint} de ${questions.length} preguntas correctas. + ${creditoAdded}€`;
        await createGananciasLog(fechaActual, pacienteId, logText)
          .then((res) => console.log("Registro de ganancias log creado:", res))
          .catch((error) =>
            console.error("Error al crear registro en ganancias log:", error)
          );

        setGananciasMessage(
          `Ha obtenido ${creditoAdded}€ y se lo hemos añadido a sus Ganancias`
        );
        setShouldShowModalAfterResults(true);
      }
    } catch (err) {
      console.log("Error al actualizar Mejorate:", err);
    } finally {
      setIsCreatingButton(false);
    }
  };

  // Determinar si se debe mostrar la introducción
  // const showIntro = parentData[`first${quizType}`] === null;
  const showIntro = false;

  // Determinar el paso inicial
  const initialStep = initialMejorateCompleted
    ? 1
    : showIntro
    ? 1
    : mejorateStep + 1;

  const handleStepChange = (stats) => {
    // stats.currentStep es el paso actual y stats.totalSteps es el total de pasos
    if (stats.currentStep === stats.totalSteps && shouldShowModalAfterResults) {
      setIsGananciasModalOpen(true);
      setShouldShowModalAfterResults(false); // Resetea el flag para evitar que se muestre nuevamente
    }
  };

  return (
    <>
      <div id="content" className="mejorate-content">
        <div id="detail">
          {isLoading ? (
            <></> // Renderizado vacío mientras se carga
          ) : quizAvailable ? ( // Mostrar contenido si hay cuestionario disponible
            <div className="cuestionario-container">
              {/* <h1>Cuestionario Mejórate. Ayúdanos a optimizar tu tratamiento y observa tu evolución. Completa el cuestionario y OBTÉN 15€ A CAMBIO.</h1> */}
              <StepWizard
                initialStep={initialStep}
                onStepChange={handleStepChange}
              >
                {showIntro && (
                  <IntroStep
                    //onComenzar={updateMejorate}
                    quizType={quizType}
                    mejorateStep={mejorateStep}
                    completed={initialMejorateCompleted}
                  />
                )}
                {questions.map((q, index) => (
                  <QuestionStep
                    key={index}
                    showIntro={showIntro}
                    question={q.question}
                    options={q.options}
                    correctAnswerIndex={q.correctAnswerIndex}
                    onAnswer={(isCorrect, isLastQuestion) =>
                      handleAnswer(isCorrect, isLastQuestion, index)
                    }
                    isLastQuestion={index === questions.length - 1}
                  />
                ))}
                <ResultsSummary
                  mejoratePoint={mejoratePoint}
                  totalQuestions={questions.length}
                />
              </StepWizard>
            </div>
          ) : (
            <div className="no-cuestionario">
              <h2>No existe cuestionario disponible para completar</h2>
            </div>
          )}
        </div>
      </div>
      <GananciasModal
        isOpen={isGananciasModalOpen}
        onClose={() => setIsGananciasModalOpen(false)}
        title="Ganancias obtenidas"
        message={gananciasMessage}
        buttonStyle="is-danger"
      />
    </>
  );
}
